import { ButtonColor } from "@/common/enum/button-color";
import { LoginPath } from "@/common/enum/path";
import { TextInputColor } from "@/common/enum/text-input-color";
import { formatBusinessNumber } from "@/common/util/format-business-number";
import { formatPhoneNumber } from "@/common/util/format-phone-number";
import { validatePhoneNumber } from "@/common/validator/validate-phone-number";
import { userService } from "@/services/user-service";
import { Button, TextInput } from "flowbite-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomHelperText } from "../CustomHelperText";
import { TextInputLabel } from "../label/TextInputLabel";
import { Body14Underline } from "../typography/Body14Underline";

interface Props {
  updateFindPartnerEmail: (email: string) => void;
}

const initFindPartnerEmailPayload = {
  businessNumber: "",
  name: "",
  phone: "",
};
export const FindIdPanel: React.FC<Props> = ({ updateFindPartnerEmail }) => {
  const navigate = useNavigate();
  const [findPartnerEmailPayload, setFindPartnerEmailPayload] = useState(
    initFindPartnerEmailPayload
  );
  const [aggregateErrors, setAggregateErrors] = useState({
    businessNumber: "",
    name: "",
    phone: "",
  });

  const isFindPartnerEmailButtonActive =
    findPartnerEmailPayload.businessNumber &&
    findPartnerEmailPayload.name &&
    findPartnerEmailPayload.phone;

  const handleFindPartnerEmailPayloadChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setFindPartnerEmailPayload({
      ...findPartnerEmailPayload,
      [name]: value,
    });
  };

  const handleBusinessNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatBusinessNumber(event.target.value);

    setFindPartnerEmailPayload({
      ...findPartnerEmailPayload,
      businessNumber: formattedValue,
    });
  };

  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formattedValue = formatPhoneNumber(event.target.value);

    setFindPartnerEmailPayload({
      ...findPartnerEmailPayload,
      phone: formattedValue,
    });
  };

  const handleFindPartnerEmailButtonClick = async () => {
    try {
      if (!validatePhoneNumber(findPartnerEmailPayload.phone)) {
        setAggregateErrors({
          ...aggregateErrors,
          phone: "올바른 휴대폰 번호를 입력해주세요",
        });
        return;
      }

      const response = await userService.findPartnerEmail(
        findPartnerEmailPayload
      );
      setAggregateErrors({
        businessNumber: response.data.aggregateErrors?.businessNumber || "",
        name: response.data.aggregateErrors?.name || "",
        phone: response.data.aggregateErrors?.phone || "",
      });
      if (response.ok) {
        updateFindPartnerEmail(response.data.email);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleLoginClick = async () => {
    navigate(LoginPath.LOGIN);
  };

  return (
    <div className="flex flex-col w-full">
      <TextInputLabel required>사업자 번호</TextInputLabel>
      <TextInput
        required
        placeholder="000-00-00000"
        name="businessNumber"
        value={findPartnerEmailPayload.businessNumber}
        onChange={handleBusinessNumberChange}
        color={
          !!aggregateErrors.businessNumber
            ? TextInputColor.FAILURE
            : TextInputColor.GRAY
        }
      />
      {!!aggregateErrors.businessNumber && (
        <CustomHelperText
          isError={!!aggregateErrors.businessNumber}
          text={aggregateErrors.businessNumber}
        />
      )}
      <div className="mt-[16px]">
        <TextInputLabel required>파트너명</TextInputLabel>
      </div>
      <TextInput
        required
        placeholder="사업자 본인 이름을 입력해주세요"
        name="name"
        value={findPartnerEmailPayload.name}
        onChange={handleFindPartnerEmailPayloadChange}
        color={
          !!aggregateErrors.name ? TextInputColor.FAILURE : TextInputColor.GRAY
        }
      />
      {!!aggregateErrors.name && (
        <CustomHelperText
          isError={!!aggregateErrors.name}
          text={aggregateErrors.name}
        />
      )}
      <div className="mt-[16px]">
        <TextInputLabel required>연락처</TextInputLabel>
      </div>
      <TextInput
        required
        placeholder="010-1234-1234"
        name="phone"
        value={findPartnerEmailPayload.phone}
        onChange={handlePhoneNumberChange}
        color={
          !!aggregateErrors.phone ? TextInputColor.FAILURE : TextInputColor.GRAY
        }
      />
      <CustomHelperText
        isError={!!aggregateErrors.phone}
        text={
          aggregateErrors.phone || "가입 시 입력한 휴대폰 번호를 입력해주세요"
        }
      />
      <Button
        className="mt-[32px]"
        color={isFindPartnerEmailButtonActive && ButtonColor.PRIMARY}
        disabled={!isFindPartnerEmailButtonActive}
        onClick={handleFindPartnerEmailButtonClick}
      >
        아이디 찾기
      </Button>
      <div className="flex justify-center mt-[24px] text-semantic-text-body">
        <div onClick={handleLoginClick}>
          <Body14Underline className="text-center">로그인 하기</Body14Underline>
        </div>
      </div>
    </div>
  );
};
